import { DatasourceCanBaseIcon } from '@tableau/icons/DatasourceCanBaseIcon';
import { DatasourceServerBaseIcon } from '@tableau/icons/DatasourceServerBaseIcon';
import { DatasourceServerConnectionBaseIcon } from '@tableau/icons/DatasourceServerConnectionBaseIcon';
import { DatasourceTableBaseIcon } from '@tableau/icons/DatasourceTableBaseIcon';
import { DatasourceTableConnectionIcon } from '@tableau/icons/DatasourceTableConnectionIcon';
import { FileBaseIcon } from '@tableau/icons/FileBaseIcon';
import { FlowBaseIcon } from '@tableau/icons/FlowBaseIcon';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { SvgSpacesMediumDs, SvgSpacesMediumExternalServer, SvgSpacesMediumFile, SvgSpacesMediumFlow, SvgSpacesMediumTable, SvgVirtualConnection, SvgVirtualConnectionTable } from '../../../../../gen-src/icons';
import { RemoteType } from '../../../../../gen-src/queries';
import { RemoteTypeIcon } from '../dataGrid/cellFormatters/RemoteTypeIcon';
import { createDefaultSiteUrls } from '../urls/SiteUrlsContext';
import { assertUnreachable } from '../utils/TypescriptHelpers';
import { HydratedCertificationRenderProps, RenderCertification } from './certification/CertificationProps';
import { HydratedSensitivityRenderProps, RenderSensitivity } from './sensitivity/SensitivityProps';
import { LabelAssetType, LabelServiceAssetType, LabelSubjectArea } from './shared/LabelAssetContent';
import { LabelCategories } from './shared/LabelCategories';
import { LabelContentItem, LabelRenderControls } from './shared/LabelRenderProps';
import { HydratedWarningRenderProps, RenderWarning } from './warning/WarningProps';


export const disabledSiteUrls = createDefaultSiteUrls('');

export const defaultLabelRenderControls: LabelRenderControls = {
  siteUrls: disabledSiteUrls,
  linksDisabled: true,
  kind: 'chip',
  popoverPlacement: 'bottom-start',
  hidePopoverWhenReferenceIsHidden: true,
};

export const LabelButtonContext = React.createContext<LabelRenderControls>(defaultLabelRenderControls);

export function buildLabelsPopoverContext(props: HydratedCertificationRenderProps | HydratedWarningRenderProps | HydratedSensitivityRenderProps): LabelsPopoverContextType {
  const totalUpstream = props.category === 'warning' || props.category === 'sensitivity' ? props.upstream?.labels.length ?? 0 : 0;
  const totalParented = props.parented?.labels.length ?? 0;
  const totalAnchored = props.anchored?.labels.length ?? 0;
  const totalLabels = (totalUpstream + totalParented + totalAnchored);

  let collapse: LabelsPopoverContextType['collapse'] = 'disallow';

  if (totalLabels > 1 && totalLabels <= 3) {
    collapse = 'allow';
  } else if (totalLabels > 3) {
    collapse = 'prefer';
  }

  return { collapse, content: props.content, category: props.category };
}

type LabelsPopoverContextType = {
  readonly content?: LabelContentItem;
} & LabelCollapseContextType & LabelCategories;

export type LabelCollapseContextType = {
  readonly collapse: 'allow' | 'prefer' | 'disallow';
}

export const LabelsPopoverContext = React.createContext<LabelsPopoverContextType>({
  collapse: 'allow',
  category: 'warning',
});

export type RenderLabelAssetProps = {
  readonly subjectArea?: LabelSubjectArea | null;
  readonly assetName: string | null;
  readonly assetType: LabelServiceAssetType | LabelAssetType | null;
  readonly assetUrl: string | null;
  readonly remoteType?: RemoteType;
  readonly parentName?: string | null;
  readonly parentUrl?: string | null;
} & RenderLabelsProps;

export type RenderAssetProps = {
  readonly assetName: string | null;
  readonly assetType: LabelServiceAssetType | LabelAssetType | null;
  readonly assetUrl: string | null;
  readonly remoteType?: RemoteType;
  readonly parentName?: string | null;
  readonly parentUrl?: string | null;
}

export type RenderLabelsProps = {
  readonly category: 'certification';
  readonly labels: ReadonlyArray<RenderCertification>;
} |
{
  readonly category: 'warning';
  readonly labels: ReadonlyArray<RenderWarning>;
} | {
  readonly category: 'sensitivity';
  readonly labels: ReadonlyArray<RenderSensitivity>;
}

export function assetIconForType(type: LabelSubjectArea | null, remoteType?: RemoteType): JSX.Element | null {
  switch (type) {
    case null:
      return null;
    case 'CloudFile': case 'File':
      return <SvgSpacesMediumFile />;
    case 'database': case 'DatabaseServer': case 'WebDataConnector':
      return <SvgSpacesMediumExternalServer />;
    case 'table': case 'DatabaseTable':
      return <SvgSpacesMediumTable />;
    case 'Flow': case 'flow':
      return <SvgSpacesMediumFlow />;
    case 'datasource': case 'PublishedDatasource': case 'EmbeddedDatasource':
      return <SvgSpacesMediumDs />;
    case 'column': case 'Column':
      return remoteType ? <RemoteTypeIcon remoteType={remoteType} /> : null;
    case 'VirtualConnection': case 'virtualConnection':
      return <SvgVirtualConnection />;
    case 'VirtualConnectionTable': case 'virtualConnectionTable':
      return <SvgVirtualConnectionTable />;
    case 'Field':
      return remoteType ? <RemoteTypeIcon remoteType={remoteType} /> : null;
    case 'View': case 'Metric': case 'Workbook':
      return null;
    default:
      assertUnreachable(type);
      return null;
  }
}

export function assetTableauIconForType(type: LabelSubjectArea | null, remoteType?: RemoteType): JSX.Element | null {
  switch (type) {
    case null:
      return null;
    case 'CloudFile': case 'File':
      return <FileBaseIcon size={14} />;
    case 'database': case 'DatabaseServer': case 'WebDataConnector':
      return <DatasourceServerBaseIcon size={14} />
    case 'table': case 'DatabaseTable':
      return <DatasourceTableBaseIcon size={14} />;
    case 'Flow': case 'flow':
      return <FlowBaseIcon size={14} />;
    case 'datasource': case 'PublishedDatasource': case 'EmbeddedDatasource':
      return <DatasourceCanBaseIcon size={14} />
    case 'column': case 'Column':
      return remoteType ? <RemoteTypeIcon remoteType={remoteType} /> : null;
    case 'VirtualConnection': case 'virtualConnection':
      return <DatasourceServerConnectionBaseIcon size={14} />;
    case 'VirtualConnectionTable': case 'virtualConnectionTable':
      return <DatasourceTableConnectionIcon size={14} />;
    case 'Field':
      return remoteType ? <RemoteTypeIcon remoteType={remoteType} /> : null;
    case 'View': case 'Metric': case 'Workbook':
      return null;
    default:
      assertUnreachable(type);
      return null;
  }
}


// TODO: refactor to use groupByToMultimap
export const groupBy = <T, K extends string>(arr: T[], key: (item: T) => K) =>
  arr.reduce((groups, item) => {
    const k = key(item);
    !groups.has(k) ? groups.set(k, [item]) : groups.get(k)?.push(item);
    return groups;
  }, new Map<K, T[]>());

export function groupLabelsByType(labels: RenderLabelAssetProps[]): RenderLabelAssetProps[][] {
  return Array.from(groupBy(labels, i => i.labels[0].type ?? '').values());
}

export function useExpand(overrides?: LabelCollapseContextType): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  const { collapse } = React.useContext(LabelsPopoverContext);
  const [isExpanded, setIsExpanded] = React.useState(() => {
    switch (overrides?.collapse ?? collapse) {
      case 'disallow': case 'allow': return true;
      case 'prefer': return false;
    }
  });
  return [isExpanded, setIsExpanded];
}

export function buildLineagePath(siteUrls: SiteUrls, source: LabelSubjectArea | undefined, vizportalUrlId: string): string {
  switch (source) {
    case 'datasource': case 'EmbeddedDatasource': case 'PublishedDatasource':
      return siteUrls.datasourceLineageFieldsUrl(vizportalUrlId);
    case 'flow': case 'Flow':
      return siteUrls.flowLineageFieldsUrl(vizportalUrlId);
    case 'virtualConnectionTable': case 'VirtualConnectionTable':
      return siteUrls.virtualConnectionTableLineageFieldsUrl(vizportalUrlId);
    case 'workbook': case 'Workbook':
      return siteUrls.workbookLineageFieldsUrl(vizportalUrlId);
    default:
      return '';
  }
}
